import { Injectable } from "@angular/core";
import { RouterState } from "@angular/router";
import { AuthUser } from "@app/auth/model/auth-user";
import { AuthActions, AuthSelectors, AuthState } from "@app/auth/store";
import { ClareSystem } from "@app/project/model/clare-system";
import { ProjectStatusService } from "@app/project/services/project-status.service";
import { RouterSelectors } from "@app/root-store";
import { LoadingState } from "@app/root-store/api-result.state";
import { User } from "@app/user/model/user";
import { Store, select } from "@ngrx/store";
import { BehaviorSubject, Observable, combineLatest, of } from "rxjs";
import { distinctUntilChanged, filter, map, switchMap, tap } from "rxjs/operators";
import { CustomerLocationActions } from "../store/actions";
import { CustomerLocationClaresystemsState, CustomerLocationState, CustomerLocationUsersState } from "../store/customer-location.state";
import { CustomerLocationSelectors } from "../store/selectors";

@Injectable({
    providedIn: "root",
})
export class CustomerPortalService {
    dispatchedCustomerRole = false;
    isFirstSignup$ = new BehaviorSubject<boolean>(false);

    constructor(
        private readonly store: Store<CustomerLocationState | AuthState | RouterState>,
        private readonly projectStatusService: ProjectStatusService
    ) {}

    dispatchAction(action) {
        this.store.dispatch(action);
    }

    logout() {
        this.store.dispatch(AuthActions.logOutConfirmation());
    }

    selectQueryParams$() {
        return this.store.pipe(select(RouterSelectors.getQueryParams));
    }

    selectAuthUser$() {
        return this.store.pipe(select(AuthSelectors.selectGetUser));
    }

    selectUserLocationAndState$() {
        return combineLatest([
            this.selectAuthUser$(),
            this.store.pipe(select(CustomerLocationSelectors.getCurrentLocation)),
            this.store.pipe(select(CustomerLocationSelectors.getCustomerLocationsState)),
        ]).pipe(
            filter(([authUser]) => !!authUser),
            tap(([authUser, , state]: [AuthUser, ClareSystem, CustomerLocationClaresystemsState]) => {
                if (state.callState === LoadingState.init) {
                    this.store.dispatch(
                        new CustomerLocationActions.GetLocationsByCustomer({
                            customerId: authUser.id,
                        })
                    );
                }
            }),
            filter(([, , state]) => state.callState === LoadingState.loaded)
        );
    }

    selectCurrentLocation$() {
        return this.selectUserLocationAndState$().pipe(map(([, currentLocation]) => currentLocation || null));
    }

    updateLocation$(claresystem: ClareSystem) {
        return this.dispatchAction(new CustomerLocationActions.UpdateLocation({ claresystem }));
    }

    getCurrentLocationOnlineStatus$() {
        return this.projectStatusService.getActiveProjectStatus$();
    }

    selectPrimaryContactAndUser$() {
        return this.selectUserLocationAndState$().pipe(
            map(([authUser, claresystem]) => {
                let primaryContact: number = null;

                if (claresystem) {
                    primaryContact = claresystem.primaryContact || null;
                }

                return { primaryContact, authUser };
            })
        );
    }

    getCustomerRoleId$() {
        return this.store.pipe(
            select(CustomerLocationSelectors.getCustomerRoleId),
            map(roleId => {
                if (!this.dispatchedCustomerRole) {
                    this.dispatchedCustomerRole = true;
                    this.store.dispatch(new CustomerLocationActions.GetCustomerRoleId());
                }
                return roleId;
            })
        );
    }

    getLocationUsersState$(uuid: string) {
        return this.store.pipe(
            select(CustomerLocationSelectors.getLocationUsersState),
            map(getUsers => getUsers(uuid))
        );
    }

    selectLocationUsersCallState$(uuid: string) {
        return this.getLocationUsersState$(uuid).pipe(map((state: CustomerLocationUsersState) => state.callState));
    }

    selectCurrentLocationUsers$() {
        return this.selectCurrentLocation$().pipe(
            filter(currentLocation => !!currentLocation),
            switchMap(currentLocation => this.selectLocationUsers$(currentLocation))
        );
    }

    selectLocationUsers$(claresystem: ClareSystem): Observable<{ userinfo: User }[]> {
        if (claresystem) {
            return this.getLocationUsersState$(claresystem.uuid).pipe(
                filter(state => !!state),
                distinctUntilChanged(),
                map((state: CustomerLocationUsersState) => {
                    if (state.callState === LoadingState.init) {
                        this.store.dispatch(
                            new CustomerLocationActions.GetLocationUsers({
                                claresystem,
                            })
                        );
                    }

                    return state;
                }),
                filter(state => state.callState === LoadingState.loaded),
                map(state => state.result || [])
            );
        }
        return of([]);
    }

    clearLocationUsers(claresystem: ClareSystem) {
        this.store.dispatch(new CustomerLocationActions.ClearLocationUsersState({ claresystem }));
    }

    inviteUser(primaryUser: User, invitedUser: User, claresystem: ClareSystem, uuid: string) {
        this.store.dispatch(
            new CustomerLocationActions.InviteUserToLocation({
                primaryUser,
                invitedUser,
                claresystem,
                uuid,
            })
        );
    }

    getLocationUuid() {
        return this.store.pipe(select(RouterSelectors.selectUrlParam("uuid")));
    }
    firstSignup(isFirstSignup: boolean) {
        this.isFirstSignup$.next(isFirstSignup);
    }
}
